export type ExperimentalFeatureId =
  | "ai-chat"
  | "connected-sources"
  | "job-control-tags"
  | "notebooks"
  | "packaging"
  | "protected-branches"
  | "resource-redesign"
  | "system-redesign";

type ExperimentalFeatureBase = {
  readonly id: ExperimentalFeatureId;
  readonly storageKey: string;
  // Counts as on only while this feature is on too; the user's own choice is kept meanwhile.
  readonly requires?: ExperimentalFeatureId;
};

type ExperimentalFeatureRollout =
  | {
      // Half-wired features stay invisible unless their id is listed in VITE_EXPERIMENTAL_AVAILABLE.
      readonly gated: true;
      readonly defaultEnabled?: never;
    }
  | {
      readonly gated?: false;
      // Counts as the environment default (like DEFAULT_ON): a stored user choice still wins, and LOCKED pins it on.
      readonly defaultEnabled?: boolean;
    };

export type ExperimentalFeature = ExperimentalFeatureBase & ExperimentalFeatureRollout;

// Insertion order drives the order of the rows on the Application Settings page.
// Keep the feature id list in .env.example in sync when adding or removing an entry.
export const EXPERIMENTAL_FEATURES: ExperimentalFeatureRegistry = {
  "system-redesign": {
    id: "system-redesign",
    storageKey: "istari-ff-system-redesign",
    defaultEnabled: true,
  },
  "resource-redesign": {
    id: "resource-redesign",
    storageKey: "istari-ff-resource-redesign",
    gated: true,
  },
  packaging: {
    id: "packaging",
    storageKey: "istari-ff-packaging",
    gated: true,
    requires: "system-redesign",
  },
  "protected-branches": {
    id: "protected-branches",
    storageKey: "istari-ff-protected-branches",
    gated: true,
    requires: "system-redesign",
  },
  notebooks: {
    id: "notebooks",
    storageKey: "istari-ff-notebooks",
  },
  "ai-chat": {
    id: "ai-chat",
    storageKey: "istari-ff-ai-chat",
  },
  "connected-sources": {
    id: "connected-sources",
    storageKey: "istari-ff-connected-sources",
  },
  // Needs the job control tags release of registry-service and istari-agent; stays invisible until an
  // environment lists it in VITE_EXPERIMENTAL_AVAILABLE.
  "job-control-tags": {
    id: "job-control-tags",
    storageKey: "istari-ff-job-control-tags",
    gated: true,
  },
};

export const EXPERIMENTAL_FEATURE_LIST: readonly ExperimentalFeature[] = Object.values(EXPERIMENTAL_FEATURES);

// Pinning `id` to the key makes a copy-pasted entry a compile error rather than a lookup that
// silently resolves the wrong feature.
type ExperimentalFeatureRegistry = {
  readonly [K in ExperimentalFeatureId]: ExperimentalFeature & { readonly id: K };
};
